import { CANONICAL_ORIGIN } from '@/config/site';
import { nativeShims } from '@/native/shims';

/**
 * referral.ts — the M101 share-to-earn plumbing (SHARER-only rewards, owner
 * rule 2026-07-28: 20 SP per friend who creates a Racer ID via the link,
 * capped at 5 friends / 100 SP — all amounts enforced SERVER-side in
 * `claim_referral`).
 *
 * Flow: a share link is `dribbleracer.com/?ref=CODE`. A visitor's boot
 * captures the code into localStorage (and strips it from the URL so it
 * never lingers in shares/history). When that visitor CREATES a Racer ID,
 * the sync engine claims the code once — the server credits the sharer and
 * rejects self/stale/replayed/capped claims. Any definitive server answer
 * clears the pending code; only a transport failure keeps it for retry.
 *
 * Known v1 limit (flagged in the plan): capture is per-browser — a friend
 * who clicks on one device but signs up on another won't credit.
 */

const REF_KEY = 'dribble-racer.ref.v1';
const REF_RE = /^[0-9A-Fa-f]{6}$/;

/** Normalize a raw ?ref value to a canonical code, or null. PURE (tested). */
export function sanitizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  return REF_RE.test(v) ? v.toUpperCase() : null;
}

/** The share URL for a profile's code. PURE (tested). */
export function referralLink(code: string): string {
  return `${CANONICAL_ORIGIN}/?ref=${code}`;
}

/** Boot-time capture: remember ?ref=CODE, then strip it from the URL (before
 *  anything else can cache/share the address). Never throws. */
export function captureReferralParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('ref')) return;
    const code = sanitizeReferralCode(url.searchParams.get('ref'));
    if (code) window.localStorage.setItem(REF_KEY, code);
    url.searchParams.delete('ref');
    window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
  } catch {
    /* storage blocked / odd URL — referrals are a bonus, never a blocker */
  }
}

/** M140b: remember a share code that reached us some other way (an iOS
 *  universal link opens the app, so the boot-time URL capture never sees
 *  it). The claim stays server-checked (new accounts only, never yourself). */
export function rememberReferral(raw: string | null): void {
  const code = sanitizeReferralCode(raw);
  if (!code) return;
  try {
    window.localStorage.setItem(REF_KEY, code);
  } catch {
    /* storage blocked — referrals are a bonus */
  }
}

export function pendingReferral(): string | null {
  try {
    return sanitizeReferralCode(window.localStorage.getItem(REF_KEY));
  } catch {
    return null;
  }
}

export function clearPendingReferral(): void {
  try {
    window.localStorage.removeItem(REF_KEY);
  } catch {
    /* ignore */
  }
}

/** The one share-copy voice, whatever link rides along (M114). */
const SHARE_TEXT = 'Race me in Dribble Racer — push your ball across the world!';

/** The sheet/clipboard plumbing every share surface rides (M114): the native
 *  Share plugin inside the app shell (WKWebView has no navigator.share),
 *  navigator.share on the mobile web, a clipboard copy on desktop. */
async function shareUrl(
  url: string,
  text: string = SHARE_TEXT,
): Promise<'shared' | 'copied' | 'dismissed'> {
  if (nativeShims.share) {
    return nativeShims.share({ title: 'Dribble Racer', text, url });
  }
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'Dribble Racer', text, url });
      return 'shared';
    } catch {
      return 'dismissed'; // user closed the sheet (or share unavailable mid-call)
    }
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    return 'copied';
  } catch {
    return 'dismissed';
  }
}

/** Open the native share sheet with the player's link (mobile), falling back
 *  to a clipboard copy (desktop). 'dismissed' = the user closed the sheet —
 *  callers show no feedback for that. */
export async function shareReferralLink(
  code: string,
): Promise<'shared' | 'copied' | 'dismissed'> {
  return shareUrl(referralLink(code));
}

/** M114 — the plain game link for players without a referral code (guests,
 *  the account kill switch): the same sheet + copy, pointing at the
 *  canonical home so a shared link never dies inside the app shell. */
export async function shareGameLink(): Promise<'shared' | 'copied' | 'dismissed'> {
  return shareUrl(`${CANONICAL_ORIGIN}/`);
}

/** M140 — share any link with its own message (the crew link names the crew
 *  and its code). Same sheet / clipboard plumbing as every share surface. */
export async function shareLinkWithText(
  url: string,
  text: string,
): Promise<'shared' | 'copied' | 'dismissed'> {
  return shareUrl(url, text);
}
